import useCardSorters from "@shared/common/CardSorters/useCardSorters";
import CardSorters from "common/CardSorters/CardSorters";
import { useCallback, useMemo, useState } from "react";
import { ColumnType } from "@shared/common/CardsList/types";
import { toggleArrayItem } from "utils/arrays";

interface useCardsSortersProps<RecordType extends { key: string | number }> {
  data: RecordType[];
  columns: Omit<ColumnType<RecordType>, "render">[];
  defaultSortFunction?: (a: RecordType, b: RecordType) => number;
  hideExpandCollapse?: boolean;
}

const useExpandedCardsSorters = <
  RecordType extends { key: string | number } = any
>({
  data,
  columns,
  defaultSortFunction,
  hideExpandCollapse,
}: useCardsSortersProps<RecordType>) => {
  const [expandedKeys, setExpandedKeys] = useState<(string | number)[]>([]);

  const keys = useMemo(() => data.map((item) => item.key), [data]);

  const { sortedData, cardsSorters } = useCardSorters({
    CardSorters,
    columns,
    onExpandAll: () => setExpandedKeys(keys),
    onCollapseAll: () => setExpandedKeys([]),
    keys,
    expandedKeys,
    data,
    showExpandCollapse: !hideExpandCollapse,
    defaultSortFunction,
    style: { padding: "8px 12px" },
  });

  const toggleExpanded = useCallback(
    (key: number | string) =>
      setExpandedKeys((expandedKeys) => toggleArrayItem(expandedKeys, key)),
    []
  );

  const isExpanded = (key: number | string) => expandedKeys.includes(key);

  return { sortedData, cardsSorters, toggleExpanded, isExpanded };
};

export default useExpandedCardsSorters;
